import { DeleteOutlined, KeyOutlined, PlusOutlined, UserOutlined } from "@ant-design/icons";
import { ProTable } from "@ant-design/pro-components";
import type { ProColumns } from "@ant-design/pro-components";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button, Drawer, Form, Input, message, Modal, Popconfirm, Select, Space, Switch, Tag,
} from "antd";
import { useState } from "react";
import {
  activateUser, createUser, deactivateUser, deleteUser, listRoles,
  listUsers, resetUserPassword, updateUser,
  type UserOut,
} from "./api";

export function UsersPage() {
  const qc = useQueryClient();
  const [msgApi, msgCtx] = message.useMessage();
  const [openCreate, setOpenCreate] = useState(false);
  const [editUser, setEditUser] = useState<UserOut | null>(null);
  const [pwdUid, setPwdUid] = useState<string | null>(null);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [pwdForm] = Form.useForm();
  const [reloadKey, setReloadKey] = useState(0);

  const rolesQ = useQuery({ queryKey: ["admin-roles"], queryFn: listRoles });

  const reload = () => {
    setReloadKey(k => k + 1);
    qc.invalidateQueries({ queryKey: ["admin-users"] });
  };

  const createM = useMutation({
    mutationFn: createUser,
    onSuccess: () => { msgApi.success("已创建"); setOpenCreate(false); createForm.resetFields(); reload(); },
    onError: (e: Error) => msgApi.error(e.message),
  });
  const updateM = useMutation({
    mutationFn: ({ uid, body }: { uid: string; body: Parameters<typeof updateUser>[1] }) =>
      updateUser(uid, body),
    onSuccess: () => { msgApi.success("已更新"); setEditUser(null); reload(); },
    onError: (e: Error) => msgApi.error(e.message),
  });
  const delM = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => { msgApi.success("已删除"); reload(); },
    onError: (e: Error) => msgApi.error(e.message),
  });
  const toggleM = useMutation({
    mutationFn: ({ uid, on }: { uid: string; on: boolean }) =>
      on ? activateUser(uid) : deactivateUser(uid),
    onSuccess: () => { msgApi.success("已更新"); reload(); },
  });
  const pwdM = useMutation({
    mutationFn: ({ uid, password }: { uid: string; password: string }) =>
      resetUserPassword(uid, password),
    onSuccess: () => { msgApi.success("密码已重置"); setPwdUid(null); pwdForm.resetFields(); },
    onError: (e: Error) => msgApi.error(e.message),
  });

  const roleOptions = (rolesQ.data ?? []).map(r => ({ value: r.code, label: `${r.name} (${r.code})` }));

  const COLUMNS: ProColumns<UserOut>[] = [
    { title: "用户名", dataIndex: "username", width: 140, copyable: true },
    { title: "姓名", dataIndex: "real_name", width: 120 },
    { title: "邮箱", dataIndex: "email", ellipsis: true },
    { title: "手机", dataIndex: "phone", width: 130 },
    {
      title: "角色", dataIndex: "roles", width: 200, search: false,
      render: (_, r) => r.roles.map(c => <Tag key={c} color="blue">{c}</Tag>),
    },
    {
      title: "启用", dataIndex: "is_active", width: 80, align: "center", search: false,
      render: (_, r) => (
        <Switch
          checked={r.is_active}
          size="small"
          onChange={(on) => toggleM.mutate({ uid: r.id, on })}
        />
      ),
    },
    {
      title: "操作", valueType: "option", width: 200,
      render: (_, r) => [
        <Button key="e" size="small" type="link" icon={<UserOutlined />}
          onClick={() => { editForm.setFieldsValue(r); setEditUser(r); }}>
          编辑
        </Button>,
        <Button key="p" size="small" type="link" icon={<KeyOutlined />}
          onClick={() => setPwdUid(r.id)}>
          改密
        </Button>,
        <Popconfirm key="d" title="删除该用户?" onConfirm={() => delM.mutate(r.id)}>
          <Button size="small" type="link" danger icon={<DeleteOutlined />}>删</Button>
        </Popconfirm>,
      ],
    },
  ];

  return (
    <div>
      {msgCtx}
      <ProTable<UserOut>
        key={reloadKey}
        headerTitle="用户管理"
        rowKey="id"
        columns={COLUMNS}
        search={{ labelWidth: 80 }}
        pagination={{ defaultPageSize: 20 }}
        toolBarRender={() => [
          <Button key="new" type="primary" icon={<PlusOutlined />}
            onClick={() => { createForm.resetFields(); setOpenCreate(true); }}>
            新增用户
          </Button>,
        ]}
        request={async (params) => {
          const { current = 1, pageSize = 20, username, real_name, ...rest } = params;
          const q = (username as string | undefined) || (real_name as string | undefined);
          const out = await listUsers({ page: current, page_size: pageSize, q, ...rest });
          return { data: out.items, total: out.total, success: true };
        }}
      />

      <Drawer
        open={openCreate}
        title="新增用户"
        width={520}
        onClose={() => setOpenCreate(false)}
      >
        <Form form={createForm} layout="vertical" onFinish={(v) => createM.mutate(v)}>
          <Form.Item name="username" label="用户名" rules={[{ required: true }]}>
            <Input placeholder="字母/数字/_-." />
          </Form.Item>
          <Form.Item name="password" label="初始密码" rules={[{ required: true, min: 6 }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item name="real_name" label="姓名">
            <Input />
          </Form.Item>
          <Form.Item name="email" label="邮箱">
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="手机">
            <Input />
          </Form.Item>
          <Form.Item name="role_codes" label="角色" initialValue={["applicant"]}>
            <Select mode="multiple" options={roleOptions} loading={rolesQ.isLoading} />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={createM.isPending}>
            创建
          </Button>
        </Form>
      </Drawer>

      <Drawer
        open={!!editUser}
        title={editUser ? `编辑:${editUser.username}` : "编辑"}
        width={520}
        onClose={() => setEditUser(null)}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={(v) => editUser && updateM.mutate({ uid: editUser.id, body: v })}
        >
          <Form.Item name="real_name" label="姓名">
            <Input />
          </Form.Item>
          <Form.Item name="email" label="邮箱">
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="手机">
            <Input />
          </Form.Item>
          <Form.Item name="role_codes" label="角色">
            <Select mode="multiple" options={roleOptions} />
          </Form.Item>
          <Form.Item name="is_active" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={updateM.isPending}>保存</Button>
            <Button onClick={() => setEditUser(null)}>取消</Button>
          </Space>
        </Form>
      </Drawer>

      <Modal
        open={pwdUid !== null}
        title="重置密码"
        onCancel={() => { setPwdUid(null); pwdForm.resetFields(); }}
        okText="重置"
        confirmLoading={pwdM.isPending}
        onOk={() => pwdForm.submit()}
      >
        <Form form={pwdForm} layout="vertical"
          onFinish={({ password }) => pwdUid && pwdM.mutate({ uid: pwdUid, password })}>
          <Form.Item name="password" label="新密码" rules={[{ required: true, min: 6 }]}>
            <Input.Password autoFocus />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
